/** Joystick dead zone and sprint threshold, as fractions of the knob's reach (spec 2p §2.3). */
export const DEAD = 0.15;
export const SPRINT_AT = 0.85;

/** Where to walk: x right, z forward, length 0–1; sprint runs at full length. */
export interface Move { x: number; z: number; sprint: boolean }
export const STILL: Move = Object.freeze({ x: 0, z: 0, sprint: false });

/** A knob offset in screen pixels (y down) to a move: the walk grows with the push, a push from 0.85 runs. */
export function stickToMove(dx: number, dy: number, reach: number): Move {
  const dist = Math.hypot(dx, dy);
  const push = Math.min(dist / reach, 1);
  if (push < DEAD) return STILL;
  const sprint = push >= SPRINT_AT;
  const mag = sprint ? 1 : (push - DEAD) / (SPRINT_AT - DEAD);
  return { x: (dx / dist) * mag, z: (-dy / dist) * mag, sprint };
}

/** What the hands ask of the walker this frame; the keyboard and the touch HUD both write it (spec 2p §3.1). */
export class InputState {
  private readonly keys = new Set<string>();
  private stick: Move = STILL;
  private jumpHeld = false;
  private jumpTapped = false;
  private lookDx = 0;
  private lookDy = 0;
  private turnYaw = 0;
  private turnPitch = 0;
  private blocked = false;

  keyDown(code: string): void { if (!this.blocked) this.keys.add(code); }
  keyUp(code: string): void { this.keys.delete(code); }
  setStick(m: Move): void { this.stick = this.blocked ? STILL : m; }
  setJump(held: boolean): void {
    this.jumpHeld = held && !this.blocked;
    if (this.jumpHeld) this.jumpTapped = true; // a tap shorter than a frame still jumps once
  }
  addLook(dx: number, dy: number): void {
    if (this.blocked) return;
    this.lookDx += dx;
    this.lookDy += dy;
  }
  takeLook(): { dx: number; dy: number } {
    const l = { dx: this.lookDx, dy: this.lookDy };
    this.lookDx = 0;
    this.lookDy = 0;
    return l;
  }
  /** Radians the phone turned (the gyro), applied as they are. */
  addTurn(yaw: number, pitch: number): void {
    if (this.blocked) return;
    this.turnYaw += yaw;
    this.turnPitch += pitch;
  }
  takeTurn(): { yaw: number; pitch: number } {
    const t = { yaw: this.turnYaw, pitch: this.turnPitch };
    this.turnYaw = 0;
    this.turnPitch = 0;
    return t;
  }
  /** A panel or the chat is up: drop what is held and take nothing new until unblocked. */
  setBlocked(b: boolean): void {
    this.blocked = b;
    if (b) this.clear();
  }
  clear(): void {
    this.keys.clear();
    this.stick = STILL;
    this.jumpHeld = false;
    this.jumpTapped = false;
    this.lookDx = 0;
    this.lookDy = 0;
    this.turnYaw = 0;
    this.turnPitch = 0;
  }
  /** Held keys win over the stick, and a key heading is always a full walk: the desktop feel is unchanged. */
  move(): Move {
    let x = 0, z = 0;
    if (this.keys.has("KeyW")) z += 1;
    if (this.keys.has("KeyS")) z -= 1;
    if (this.keys.has("KeyD")) x += 1;
    if (this.keys.has("KeyA")) x -= 1;
    if (x || z) {
      const l = Math.hypot(x, z);
      return { x: x / l, z: z / l, sprint: this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") };
    }
    return this.stick;
  }
  /** Read once per frame by the walker: it also hands out a tap that was already let go. */
  jump(): boolean {
    const j = this.keys.has("Space") || this.jumpHeld || this.jumpTapped;
    this.jumpTapped = false;
    return j;
  }
}

/** The one the lobby's and the room's players share; only the enabled one reads it. */
export const input = new InputState();

const bound = new WeakSet<InputState>();

/** Keys into an InputState, once per state. Typing in a text field is not walking. */
export function bindKeyboard(s: InputState, target: Window = window): void {
  if (bound.has(s)) return;
  bound.add(s);
  target.addEventListener("keydown", (e) => { if (!isTyping()) s.keyDown(e.code); });
  target.addEventListener("keyup", (e) => s.keyUp(e.code));
  target.addEventListener("blur", () => s.clear());
}

function isTyping(): boolean {
  const el = document.activeElement;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
}
